<template>
  <div class="Profile__General__Page">
    <div class="Profile__General__Page__Header">
      <h1>הגדרות לקורס {{ course.name }}</h1>
      <div class="loading">
        <b-loading :is-full-page="true" v-model="isLoading" :can-cancel="false" />
      </div>
    </div>
    <b-field style="margin-top: 10px;" label="סוג קורס" :message="courseTypeMessage">
      <b-select v-model="model.courseTypeId" :loading="courseTypesLoading" placeholder="— בחר סוג קורס —" expanded>
        <option :value="null" disabled>— בחר סוג קורס —</option>
        <option v-for="courseType in courseTypes" :key="courseType.id" :value="courseType.id">
          {{ courseType.name }}
        </option>
      </b-select>
    </b-field>
    <b-field style="margin-top: 10px;" label="הקורס הקודם" :message="previousCourseMessage">
      <v-select style="min-width: 300px;" dir="rtl" v-model="model.previousCourseId" :loading="previousCoursesLoading"
        :filterable="true" :clearable="true" :searchable="true" :close-on-select="true" :reduce="item => item.id"
        :getOptionLabel="(item) => `${item.id} - ${item.name}`" :options="previousCourseOptions"
        placeholder="— ללא קורס קודם —">
      </v-select>
    </b-field>
    <b-field style="margin-top: 10px;" label="סטטוס קורס">
      <b-checkbox v-model="model.status" label="" type="is-link is-light" class="checkbox">
        {{ model.status ? ' ON' : 'OFF' }}
      </b-checkbox>
    </b-field>
    <b-field style="margin-top: 10px;" label="הראה דף בית חדש">
      <b-checkbox v-model="model.showNewHomePage" label="" type="is-link is-light" class="checkbox">
        {{ model.showNewHomePage ? ' ON' : 'OFF' }}
      </b-checkbox>
    </b-field>
    <b-field style="margin-top: 10px;" label="הראה תכנית דינמית">
      <b-checkbox v-model="model.showDynamicProgram" label="" type="is-link is-light" class="checkbox">
        {{ model.showDynamicProgram ? ' ON' : 'OFF' }}
      </b-checkbox>
    </b-field>
    <div class="Profile__General__Page__Form">
      <b-field label="על הקורס">
        <div class="item">
          <!-- <ckeditor :editor="editor" v-model="model.description" :config="editorConfig"></ckeditor> -->
          <NewActivityEditor v-if="ready" @onEditorChange="setDescription" :ckEditorContent="model.description"
            :content="model.description" ref="description" />
        </div>
      </b-field>

      <b-field label="סילבוס">
        <div class="item">
          <!-- <ckeditor :editor="editor" v-model="model.syllabus" :config="editorConfig"></ckeditor> -->
          <NewActivityEditor v-if="ready" @onEditorChange="setSyllabus" :ckEditorContent="model.syllabus"
            :content="model.syllabus" ref="syllabus" />
        </div>
      </b-field>

      <b-field>
        <template #label>
          <div class="field-label-with-history">
            <span>פתיח</span>
            <b-button size="is-small" type="is-text" @click="showHistory('openingRemarks', 'פתיח')"
              title="היסטוריית שינויים">
              <b-icon icon="history" size="is-small"></b-icon>
            </b-button>
          </div>
        </template>
        <div class="item">
          <!-- <ckeditor :editor="editor" v-model="model.openingRemarks" :config="editorConfig"></ckeditor> -->
          <NewActivityEditor v-if="ready" @onEditorChange="setOpeningRemarks" :ckEditorContent="model.openingRemarks"
            :content="model.openingRemarks" ref="openingRemarks" />
        </div>
      </b-field>

      <b-field label="קבצים">
        <div class="item">
          <!-- <ckeditor :editor="editor" v-model="model.courseFilesContent" :config="editorConfig"></ckeditor> -->
          <NewActivityEditor v-if="ready" @onEditorChange="setFiles" :ckEditorContent="model.courseFilesContent"
            :content="model.courseFilesContent" ref="courseFilesContent" />
        </div>
      </b-field>

      <b-field label="תנאי שימוש">
        <div class="item">
          <!-- <ckeditor :editor="editor" v-model="model.termsOfUse" :config="editorConfig"></ckeditor> -->
          <NewActivityEditor v-if="ready" @onEditorChange="setTerms" :ckEditorContent="model.termsOfUse"
            :content="model.termsOfUse" ref="termsOfUse" />
        </div>
      </b-field>
      <b-field label="ניהול משפטי מוטיבציה">
        <b-checkbox v-model="model.showMotivationText">
          הצגת משפטי מוטיבציה בעמוד הבית של הקורס
        </b-checkbox>
      </b-field>
    </div>
    <b-button class="save-btn" type="is-info" @click="updateCourse">
      שמור
    </b-button>

    <b-modal v-model="isHistoryModalActive" :width="800" scroll="keep">
      <DialogContentHistory :isActive="isHistoryModalActive" :courseId="course.id" :fieldName="currentHistoryField"
        :fieldLabel="currentHistoryLabel" @close="closeHistoryModal" @restore="restoreContent" />
    </b-modal>
  </div>
</template>

<script>
import { ToastProgrammatic as Toast } from "buefy";
// import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import vSelect from "vue-select";
import "vue-select/src/scss/vue-select.scss";
import AdminService from "../services/AdminService";
import CoursesService from "../services/CoursesService";
import NewActivityEditor from "../components/NewActivityEditor.vue";
import DialogContentHistory from "../components/DialogContentHistory.vue";

const {
  ClassicEditor,
  Alignment,
  // Autoformat, // Disabled to fix asterisk auto-formatting
  Base64UploadAdapter,
  AutoImage,
  AutoLink,
  Autosave,
  BalloonToolbar,
  BlockQuote,
  Bold,
  Bookmark,
  CloudServices,
  Code,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  HtmlComment,
  HtmlEmbed,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  //
  MediaEmbed,
  Paragraph,
  PasteFromMarkdownExperimental,
  PasteFromOffice,
  RemoveFormat,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline
} = CKEDITOR;

export default {
  name: "ProfileGeneral",
  components: {
    NewActivityEditor,
    DialogContentHistory,
    vSelect
  },
  data() {
    return {
      isLoading: null,
      ready: false,
      isHistoryModalActive: false,
      currentHistoryField: '',
      currentHistoryLabel: '',
      courseTypes: [],
      courseTypesLoading: false,
      previousCourses: [],
      previousCoursesLoading: false,
      model: {
        status: false,
        courseTypeId: null,
        previousCourseId: null,
        description: '',
        syllabus: '',
        openingRemarks: '',
        courseFilesContent: '',
        termsOfUse: '',
        ShowMotivationText: null,
        showNewHomePage: false,
        showDynamicProgram: false
      },
      editor: ClassicEditor,
      editorConfig: {
        language: 'he',
        toolbar: {
          items: [
            'heading',
            '|',
            'fontSize',
            'fontFamily',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'bold',
            'italic',
            'underline',
            '|',
            'link',
            'insertTable',
            'highlight',
            '|',
            'alignment',
            '|',
            'bulletedList',
            'numberedList',
            'todoList',
            'outdent',
            'indent'
          ],
          shouldNotGroupWhenFull: false
        },
        plugins: [
          Alignment,
          // Autoformat, // Disabled to fix asterisk auto-formatting
          Base64UploadAdapter,
          AutoImage,
          AutoLink,
          Autosave,
          BalloonToolbar,
          BlockQuote,
          Bold,
          Bookmark,
          CloudServices,
          Code,
          Essentials,
          FontBackgroundColor,
          FontColor,
          FontFamily,
          FontSize,
          GeneralHtmlSupport,
          Heading,
          Highlight,
          HorizontalLine,
          HtmlComment,
          HtmlEmbed,
          ImageBlock,
          ImageCaption,
          ImageInline,
          ImageInsertViaUrl,
          ImageResize,
          ImageStyle,
          ImageTextAlternative,
          ImageToolbar,
          ImageUpload,
          Indent,
          IndentBlock,
          Italic,
          Link,
          LinkImage,
          List,
          ListProperties,
          //
          MediaEmbed,
          Paragraph,
          PasteFromMarkdownExperimental,
          PasteFromOffice,
          RemoveFormat,
          SpecialCharacters,
          SpecialCharactersArrows,
          SpecialCharactersCurrency,
          SpecialCharactersEssentials,
          SpecialCharactersLatin,
          SpecialCharactersMathematical,
          SpecialCharactersText,
          Strikethrough,
          Style,
          Subscript,
          Superscript,
          Table,
          TableCaption,
          TableCellProperties,
          TableColumnResize,
          TableProperties,
          TableToolbar,
          TextTransformation,
          TodoList,
          Underline
        ],
        balloonToolbar: ['bold', 'italic', '|', 'link', '|', 'bulletedList', 'numberedList'],
        fontFamily: {
          supportAllValues: true
        },
        fontSize: {
          options: [10, 12, 14, 'default', 18, 20, 22],
          supportAllValues: true
        },
        heading: {
          options: [
            {
              model: 'paragraph',
              title: 'Paragraph',
              class: 'ck-heading_paragraph'
            },
            {
              model: 'heading1',
              view: 'h1',
              title: 'Heading 1',
              class: 'ck-heading_heading1'
            },
            {
              model: 'heading2',
              view: 'h2',
              title: 'Heading 2',
              class: 'ck-heading_heading2'
            },
            {
              model: 'heading3',
              view: 'h3',
              title: 'Heading 3',
              class: 'ck-heading_heading3'
            },
            {
              model: 'heading4',
              view: 'h4',
              title: 'Heading 4',
              class: 'ck-heading_heading4'
            },
            {
              model: 'heading5',
              view: 'h5',
              title: 'Heading 5',
              class: 'ck-heading_heading5'
            },
            {
              model: 'heading6',
              view: 'h6',
              title: 'Heading 6',
              class: 'ck-heading_heading6'
            }
          ]
        },
        htmlSupport: {
          allow: [
            {
              name: /^.*$/,
              styles: true,
              attributes: true,
              classes: true
            }
          ]
        },
        image: {
          toolbar: [
            'toggleImageCaption',
            'imageTextAlternative',
            '|',
            'imageStyle:inline',
            'imageStyle:wrapText',
            'imageStyle:breakText',
            '|',
            'resizeImage'
          ]
        },
        licenseKey: process.env.VUE_APP_CKEDITOR_LICENSE_KEY,
        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: 'https://',
          decorators: {
            toggleDownloadable: {
              mode: 'manual',
              label: 'Downloadable',
              attributes: {
                download: 'file'
              }
            }
          }
        },
        list: {
          properties: {
            styles: true,
            startIndex: true,
            reversed: true
          }
        },
        menuBar: {
          isVisible: true
        },
        placeholder: '',
        style: {
          definitions: [
            {
              name: 'Article category',
              element: 'h3',
              classes: ['category']
            },
            {
              name: 'Title',
              element: 'h2',
              classes: ['document-title']
            },
            {
              name: 'Subtitle',
              element: 'h3',
              classes: ['document-subtitle']
            },
            {
              name: 'Info box',
              element: 'p',
              classes: ['info-box']
            },
            {
              name: 'Side quote',
              element: 'blockquote',
              classes: ['side-quote']
            },
            {
              name: 'Marker',
              element: 'span',
              classes: ['marker']
            },
            {
              name: 'Spoiler',
              element: 'span',
              classes: ['spoiler']
            },
            {
              name: 'Code (dark)',
              element: 'pre',
              classes: ['fancy-code', 'fancy-code-dark']
            },
            {
              name: 'Code (bright)',
              element: 'pre',
              classes: ['fancy-code', 'fancy-code-bright']
            }
          ]
        },
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
        }
      },
    }
  },
  computed: {
    course() {
      return this.$store.state.auth?.course;
    },
    courseTypeMessage() {
      return this.model.courseTypeId
        ? "קובע איזה לוגו יוצג לסטודנט"
        : "לא נבחר סוג קורס — הסטודנט יראה את לוגו ברירת המחדל";
    },
    previousCourseOptions() {
      return this.previousCourses.filter((c) => c.id !== this.course.id);
    },
    previousCourseMessage() {
      const help = "הקורס שממנו החישוב הלילי של ממוצע הציונים לוקח את הממוצע לתרגולים שפחות מ-100 סטודנטים פתרו.";
      return this.model.previousCourseId
        ? help
        : `${help} לא נבחר קורס קודם — חישוב אוטומטי לא יעדכן תרגולים עם פחות מ-100 פותרים.`;
    }
  },
  created() {
    this.isLoading = true;
    AdminService.getCourseSettings(this.course.id)
      .then((r) => {
        console.log( r.data)
        this.model = r.data;
        this.isLoading = false;
        this.ready = true;
      });
    this.loadCourseTypes();
    this.loadPreviousCourses();
  },
  methods: {
    loadPreviousCourses() {
      this.previousCoursesLoading = true;
      // activeOnly=false: a previous course is usually a finished (inactive) course
      CoursesService.getCourses(false)
        .then((r) => {
          this.previousCourses = r.data;
        })
        .catch(() => {
          Toast.open({
            type: "is-danger",
            message: "טעינת רשימת הקורסים נכשלה.",
            position: "is-bottom",
            duration: 8000,
          });
        })
        .finally(() => {
          this.previousCoursesLoading = false;
        });
    },
    loadCourseTypes() {
      this.courseTypesLoading = true;
      AdminService.getCourseTypes()
        .then((r) => {
          this.courseTypes = r.data;
        })
        .catch(() => {
          Toast.open({
            type: "is-danger",
            message: "טעינת סוגי הקורס נכשלה.",
            position: "is-bottom",
            duration: 8000,
          });
        })
        .finally(() => {
          this.courseTypesLoading = false;
        });
    },
    updateCourse() {
      // this.model.description = this.$refs.description.getHTML();
      // this.model.syllabus = this.$refs.syllabus.getHTML();
      // this.model.openingRemarks = this.$refs.openingRemarks.getHTML();
      // this.model.courseFilesContent = this.$refs.courseFilesContent.getHTML();
      // this.model.termsOfUse = this.$refs.termsOfUse.getHTML();
      this.isLoading = true;
      AdminService.updateCourseGeneralSettings(this.course.id, {
        ...this.model,
        // the API ignores null, so "no previous course" must be sent as 0 to clear it
        previousCourseId: this.model.previousCourseId || 0
      })
        .then((r) => {
          this.model = r.data;
          Toast.open({
            type: "is-success",
            message: "הפרטים נשמרו בהצלחה!",
            position: "is-top",
            duration: 4000,
          });
        }).catch((error) => {
          const serverMessage =
            typeof error?.response?.data === "string" ? error.response.data : null;
          Toast.open({
            type: "is-danger",
            message: serverMessage || "הפעולה לא התבצעה. יש לנסות שוב.",
            position: "is-bottom",
            duration: 8000,
          });
        }).finally(() => {
          this.isLoading = false;
        });
    },
    setDescription(value) {
      this.model.description = value;
    },
    setSyllabus(value) {
      this.model.syllabus = value;
    },
    setOpeningRemarks(value) {
      this.model.openingRemarks = value;
    },
    setFiles(value) {
      this.model.courseFilesContent = value;
    },
    setTerms(value) {
      this.model.termsOfUse = value;
    },
    showHistory(fieldName, fieldLabel) {
      this.currentHistoryField = fieldName;
      this.currentHistoryLabel = fieldLabel;
      this.isHistoryModalActive = true;
    },
    closeHistoryModal() {
      this.isHistoryModalActive = false;
      this.currentHistoryField = '';
      this.currentHistoryLabel = '';
    },
    restoreContent({ fieldName, content }) {
      // Update the model
      this.model[fieldName] = content;

      // Update the editor component
      this.$nextTick(() => {
        if (this.$refs[fieldName]) {
          this.$refs[fieldName].setData(content);
        }
      });

      Toast.open({
        type: "is-success",
        message: "התוכן שוחזר בהצלחה! אל תשכח לשמור את השינויים.",
        position: "is-top",
        duration: 5000,
      });
    },
    save() {
      this.isLoading = true;
      this.updateGeneral(this.model);
      this.saveProfile()
        .then(() => {
          Toast.open({
            type: "is-success",
            message: "הפרטים נשמרו בהצלחה!",
            position: "is-top",
            duration: 4000,
          });
        })
        .catch(() => {
          Toast.open({
            type: "is-danger",
            message: "הפעולה לא התבצעה. יש לנסות שוב.",
            position: "is-bottom",
            duration: 8000,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  watch: {
  },
};
</script>

<style lang="scss" scoped>
.item {
  max-height: 400px;
  overflow: auto;
}

.Profile__General__Page {
  padding: 20px 40px;

  .Profile__General__Page__Header {
    border-bottom: 1px solid silver;
    padding: 0px 10px 10px 10px;

    h1 {
      font-size: 30px;
    }
  }

  .Profile__General__Page__Form {
    margin-top: 20px;
    flex-wrap: wrap;
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 30px;
    max-width: 1200px;

    .field-label-with-history {
      display: flex;
      align-items: center;
      gap: 8px;

      span {
        font-size: 15px;
        color: rgb(44, 62, 80);
      }

      button {
        padding: 0;
        height: auto;
        color: #3273dc;

        &:hover {
          color: #276cda;
        }
      }
    }

    .Profile__General__Page__Form__Field {
      margin-top: 20px;

      ::v-deep {
        .field label {
          font-size: 15px;
          color: rgb(44, 62, 80);
        }

        input {
          background-color: #f5f8fa;
          box-shadow: none;
          border: 1px solid #cbd6e2;

          &:focus {
            border: 2px solid #5fe0ea;
          }
        }
      }
    }

    .Profile__General__Page__Form__Actions {
      ::v-deep .button {
        margin-inline-end: 5px;
        background-color: #4d6884;
        color: white;
        border: none;
        border-radius: 3px;
        padding: 6px 15px;
        line-height: normal;
        height: unset;
        cursor: pointer;
        box-shadow: none;
        transition: transfrom 0.2s linear;

        &:hover {
          background-color: #54779c;
          border: none;
          outline: none;
        }

        &:active {
          transform: scale(0.98);
          border: none;
          outline: none;
        }
      }
    }
  }
}
</style>
